import { useQuery } from '@tanstack/react-query';
import { AdminCrudPage, type AdminField } from '../../components/AdminCrudPage';
import { api } from '../../lib/useEntity';
import { Card } from '../../components/ui/Card';
import { type Column } from '../../components/DataTable';
import { fmt } from '../../lib/formatters';

// ───────── Clientes ─────────

const clientesFields: AdminField[] = [
  { key: 'codigo', label: 'Código', type: 'text', required: true, hint: 'En mayúsculas, sin espacios. Ej: GAME, TICNOVA.' },
  { key: 'nombre', label: 'Nombre', type: 'text', required: true },
  {
    key: 'aplica_costes_new_product',
    label: 'Aplica coste vídeo y traducción',
    type: 'boolean',
    defaultValue: true,
    hint: 'Si está activo, las cotizaciones de este cliente suman vídeo y traducción al COSTE NEW PRODUCT. GAME = sí; TICNOVA = no.',
  },
  { key: 'activo', label: 'Activo', type: 'boolean', defaultValue: true },
];

export function ClientesAdmin() {
  return <AdminCrudPage title="Clientes" subtitle="Compañías a las que Approx vende productos." endpoint="/admin/clientes" fields={clientesFields} />;
}

// ───────── Marcas ─────────

const marcasFields: AdminField[] = [
  { key: 'cliente_id', label: 'Cliente', type: 'select', required: true, selectFrom: { endpoint: '/admin/clientes', valueKey: 'id', labelKey: 'nombre' } },
  { key: 'codigo', label: 'Código', type: 'text', required: true },
  { key: 'nombre', label: 'Nombre', type: 'text', required: true },
  { key: 'activo', label: 'Activo', type: 'boolean', defaultValue: true },
];

export function MarcasAdmin() {
  return <AdminCrudPage title="Marcas" subtitle="Marcas comercializadas por cada cliente." endpoint="/admin/marcas" fields={marcasFields} />;
}

// ───────── Categorías ─────────

const categoriasFields: AdminField[] = [
  { key: 'nombre', label: 'Nombre', type: 'text', required: true },
  { key: 'orden', label: 'Orden', type: 'number', defaultValue: 100 },
  { key: 'coste_transporte_base_eur', label: 'Coste transporte base (€/ud)', type: 'number', hint: 'Fallback general. Se usa si subcategoría, detalle y producto no lo definen.' },
  { key: 'activo', label: 'Activo', type: 'boolean', defaultValue: true },
];

export function CategoriasAdmin() {
  return (
    <AdminCrudPage
      title="Categorías"
      subtitle="Árbol de primer nivel para clasificar productos. Selecciona varias para fusionar."
      endpoint="/admin/categorias"
      fields={categoriasFields}
      mergeEndpoint="/admin/categorias/fusionar"
      mergeNoun="categoría"
    />
  );
}

// ───────── Subcategorías ─────────

const subcategoriasFields: AdminField[] = [
  { key: 'categoria_id', label: 'Categoría', type: 'select', required: true, selectFrom: { endpoint: '/admin/categorias', valueKey: 'id', labelKey: 'nombre' } },
  { key: 'nombre', label: 'Nombre', type: 'text', required: true },
  { key: 'orden', label: 'Orden', type: 'number', defaultValue: 100 },
  { key: 'coste_transporte_base_eur', label: 'Coste transporte base (€/ud, a 4.200€ contenedor)', type: 'number', hint: 'Referencia genérica para esta subcategoría.' },
  { key: 'units_contenedor_40hq', label: 'Unidades por 40HQ', type: 'number', hint: 'Cuántas unidades caben en un contenedor 40HQ.' },
  { key: 'arancel_pct', label: 'Arancel (%)', type: 'number', hint: 'Ej: 12 = 12%.' },
  { key: 'activo', label: 'Activo', type: 'boolean', defaultValue: true },
];

type SubcategoriaFullRow = {
  id: string;
  categoria_id: string;
  codigo: string;
  nombre: string;
  orden: number;
  activo: boolean;
  coste_transporte_base_eur: number | null;
  units_contenedor_40hq: number | null;
  arancel_pct: number | null;
  categoria_codigo: string | null;
  categoria_nombre: string | null;
  coste_transporte_actual_eur: number | null;
};

type SubcategoriasFullResponse = {
  data: SubcategoriaFullRow[];
  contexto: { precio_contenedor_eur: number; precio_contenedor_referencia_eur: number };
};

export function SubcategoriasAdmin() {
  // Cargamos el contexto (precio contenedor) para la info de cálculo
  // arriba — la tabla principal viene del listado/full que ya trae las
  // columnas calculadas.
  const ctxQuery = useQuery({
    queryKey: ['admin', 'subcategorias', 'contexto'],
    queryFn: () => api.get<SubcategoriasFullResponse>('/admin/subcategorias/listado/full'),
  });
  const ctx = ctxQuery.data?.contexto;

  // Opciones del filtro "Categoría" — derivadas de las propias filas para
  // que solo aparezcan las categorías que tienen al menos una subcategoría.
  const filtroCategoriaOpts = (() => {
    const rows = ctxQuery.data?.data ?? [];
    const seen = new Map<string, string>();
    for (const r of rows) {
      if (r.categoria_id && r.categoria_nombre && !seen.has(r.categoria_id)) {
        seen.set(r.categoria_id, r.categoria_nombre);
      }
    }
    return Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es'));
  })();

  const columns: Column<SubcategoriaFullRow>[] = [
    { key: 'categoria_nombre', label: 'Categoría', sortValue: (r) => r.categoria_nombre ?? '' },
    { key: 'nombre', label: 'Subcategoría', sortValue: (r) => r.nombre },
    { key: 'coste_transporte_base_eur', label: 'Coste base €/ud', align: 'right', render: (r) => fmt.eur(r.coste_transporte_base_eur), sortValue: (r) => Number(r.coste_transporte_base_eur) },
    { key: 'units_contenedor_40hq', label: 'Uds/40HQ', align: 'right', render: (r) => fmt.number(r.units_contenedor_40hq), sortValue: (r) => Number(r.units_contenedor_40hq) },
    { key: 'coste_transporte_actual_eur', label: 'Coste actual €/ud', align: 'right', render: (r) => <span className="font-semibold">{fmt.eur(r.coste_transporte_actual_eur)}</span>, sortValue: (r) => Number(r.coste_transporte_actual_eur) },
    { key: 'arancel_pct', label: 'Arancel %', align: 'right', render: (r) => fmt.pct(r.arancel_pct), sortValue: (r) => Number(r.arancel_pct) },
  ];

  return (
    <AdminCrudPage<SubcategoriaFullRow>
      title="Subcategorías"
      subtitle="Segundo nivel del árbol de productos. Selecciona varias para fusionar."
      endpoint="/admin/subcategorias"
      listEndpoint="/admin/subcategorias/listado/full"
      fields={subcategoriasFields}
      columns={columns}
      mergeEndpoint="/admin/subcategorias/fusionar"
      mergeNoun="subcategoría"
      tableFilters={[
        { key: 'categoria_id', label: 'Categoría', options: filtroCategoriaOpts },
      ]}
      headerExtra={ctx ? (
        <div className="mb-4">
          <Card title="Cálculo del coste actual">
            <p className="text-sm text-slate-600">
              Coste actual = <span className="font-mono">precio_contenedor_eur ({fmt.eur(ctx.precio_contenedor_eur)}) / uds_40HQ</span> si hay uds; en su defecto,
              <span className="font-mono"> coste_base × (precio_actual / precio_referencia ({fmt.eur(ctx.precio_contenedor_referencia_eur)}))</span>.
              Cambia <code>precio_contenedor_eur</code> en Parámetros globales para recalcular.
            </p>
          </Card>
        </div>
      ) : null}
    />
  );
}

// ───────── Detalles (cuarta capa: cuelga de subcategoría) ─────────

const detallesFields: AdminField[] = [
  { key: 'subcategoria_id', label: 'Subcategoría', type: 'select', required: true, selectFrom: { endpoint: '/admin/subcategorias', valueKey: 'id', labelKey: 'nombre' } },
  { key: 'nombre', label: 'Nombre', type: 'text', required: true },
  { key: 'orden', label: 'Orden', type: 'number', defaultValue: 100 },
  { key: 'coste_transporte_base_eur', label: 'Coste transporte base (€/ud)', type: 'number', hint: 'Override del detalle. Si está, gana frente a subcategoría/categoría.' },
  { key: 'arancel_pct', label: 'Arancel (%)', type: 'number', hint: 'Override del detalle. Si está, gana frente a la subcategoría. Déjalo vacío para heredar.' },
  { key: 'units_contenedor_40hq', label: 'Unidades por 40HQ', type: 'number', hint: 'Override del detalle sobre la subcategoría. Déjalo vacío para heredar.' },
  { key: 'activo', label: 'Activo', type: 'boolean', defaultValue: true },
  { key: 'notas', label: 'Notas', type: 'textarea' },
];

type DetalleFullRow = {
  id: string;
  subcategoria_id: string;
  codigo: string;
  nombre: string;
  orden: number;
  activo: boolean;
  notas: string | null;
  coste_transporte_base_eur: number | null;
  arancel_pct: number | null;
  units_contenedor_40hq: number | null;
  subcategoria_codigo: string | null;
  subcategoria_nombre: string | null;
  subcategoria_units_contenedor_40hq: number | null;
  subcategoria_coste_transporte_base_eur: number | null;
  subcategoria_arancel_pct: number | null;
  coste_transporte_base_efectivo_eur: number | null;
  arancel_pct_efectivo: number | null;
  units_contenedor_40hq_efectivo: number | null;
  coste_transporte_actual_eur: number | null;
  categoria_id: string | null;
  categoria_codigo: string | null;
  categoria_nombre: string | null;
};

type DetallesFullResponse = {
  data: DetalleFullRow[];
  contexto: { precio_contenedor_eur: number; precio_contenedor_referencia_eur: number };
};

export function DetallesAdmin() {
  const ctxQuery = useQuery({
    queryKey: ['admin', 'detalles', 'contexto'],
    queryFn: () => api.get<DetallesFullResponse>('/admin/detalles/listado/full'),
  });
  const ctx = ctxQuery.data?.contexto;

  const columns: Column<DetalleFullRow>[] = [
    { key: 'categoria_nombre', label: 'Categoría', render: (r) => r.categoria_nombre ?? '—', sortValue: (r) => r.categoria_nombre ?? '' },
    { key: 'subcategoria_nombre', label: 'Subcategoría', render: (r) => r.subcategoria_nombre ?? '—', sortValue: (r) => r.subcategoria_nombre ?? '' },
    { key: 'nombre', label: 'Detalle', sortValue: (r) => r.nombre },
    {
      key: 'coste_transporte_base_efectivo_eur',
      label: 'Coste base €/ud',
      align: 'right',
      render: (r) => {
        const base = r.coste_transporte_base_efectivo_eur;
        const own = r.coste_transporte_base_eur != null;
        return (
          <span title={own ? 'Override del detalle' : `Heredado de subcategoría (${fmt.eur(r.subcategoria_coste_transporte_base_eur)})`}>
            {fmt.eur(base)}{!own && base != null && <span className="ml-1 text-[10px] text-slate-400">(s)</span>}
          </span>
        );
      },
      sortValue: (r) => Number(r.coste_transporte_base_efectivo_eur),
    },
    {
      key: 'units_contenedor_40hq_efectivo',
      label: 'Uds/40HQ',
      align: 'right',
      render: (r) => {
        const eff = r.units_contenedor_40hq_efectivo;
        const own = r.units_contenedor_40hq != null;
        return (
          <span title={own ? 'Override del detalle' : `Heredado de subcategoría (${fmt.number(r.subcategoria_units_contenedor_40hq)})`}>
            {fmt.number(eff)}{!own && eff != null && <span className="ml-1 text-[10px] text-slate-400">(s)</span>}
          </span>
        );
      },
      sortValue: (r) => Number(r.units_contenedor_40hq_efectivo),
    },
    {
      key: 'coste_transporte_actual_eur',
      label: 'Coste actual €/ud',
      align: 'right',
      render: (r) => <span className="font-semibold">{fmt.eur(r.coste_transporte_actual_eur)}</span>,
      sortValue: (r) => Number(r.coste_transporte_actual_eur),
    },
    {
      key: 'arancel_pct_efectivo',
      label: 'Arancel %',
      align: 'right',
      render: (r) => {
        const eff = r.arancel_pct_efectivo;
        const own = r.arancel_pct != null;
        return (
          <span title={own ? 'Override del detalle' : `Heredado de subcategoría (${fmt.pct(r.subcategoria_arancel_pct)})`}>
            {fmt.pct(eff)}{!own && eff != null && <span className="ml-1 text-[10px] text-slate-400">(s)</span>}
          </span>
        );
      },
      sortValue: (r) => Number(r.arancel_pct_efectivo),
    },
    { key: 'orden', label: 'Orden', align: 'right', render: (r) => r.orden, sortValue: (r) => r.orden },
    { key: 'activo', label: 'Activo', render: (r) => r.activo ? 'Sí' : <span className="text-rose-600">No</span>, sortValue: (r) => (r.activo ? 1 : 0) },
  ];

  return (
    <AdminCrudPage<DetalleFullRow>
      title="Detalles"
      subtitle="Cuarta capa del árbol: GAMA → CATEGORÍA → SUBCATEGORÍA → DETALLE. Selecciona varios para fusionar."
      endpoint="/admin/detalles"
      listEndpoint="/admin/detalles/listado/full"
      fields={detallesFields}
      columns={columns}
      mergeEndpoint="/admin/detalles/fusionar"
      mergeNoun="detalle"
      headerExtra={ctx ? (
        <div className="mb-4">
          <Card title="Cálculo del coste actual">
            <p className="text-sm text-slate-600">
              Mismo cálculo que en subcategorías: <span className="font-mono">precio_contenedor_eur ({fmt.eur(ctx.precio_contenedor_eur)}) / uds_40HQ</span> si hay uds; en su defecto,
              <span className="font-mono"> coste_base × (precio_actual / precio_referencia ({fmt.eur(ctx.precio_contenedor_referencia_eur)}))</span>.
              El detalle puede sobreescribir el coste base; en caso contrario hereda el de su subcategoría (marcado con <span className="font-mono">(s)</span>).
            </p>
          </Card>
        </div>
      ) : null}
    />
  );
}

// ───────── Gamas ─────────

const gamasFields: AdminField[] = [
  { key: 'nombre', label: 'Nombre', type: 'text', required: true },
  { key: 'orden', label: 'Orden', type: 'number', defaultValue: 100 },
  { key: 'activo', label: 'Activo', type: 'boolean', defaultValue: true },
];

export function GamasAdmin() {
  return (
    <AdminCrudPage
      title="Gamas"
      subtitle="Clasificación superior de productos (OFFICE, PC-GAMING, SPORTS, LIFESTYLE, PROFESIONAL, CONSOLE). Selecciona varias para fusionar."
      endpoint="/admin/gamas"
      fields={gamasFields}
      mergeEndpoint="/admin/gamas/fusionar"
      mergeNoun="gama"
    />
  );
}

// ───────── Estados de producto ─────────

const productoEstadosFields: AdminField[] = [
  { key: 'codigo', label: 'Código', type: 'text', required: true, hideUpdate: true, hint: 'No editable tras crear.' },
  { key: 'nombre', label: 'Nombre', type: 'text', required: true },
  { key: 'orden', label: 'Orden', type: 'number', defaultValue: 100 },
  { key: 'color', label: 'Color', type: 'color', defaultValue: '#64748b' },
  { key: 'es_activo', label: 'Es un estado activo (no borrado)', type: 'boolean', defaultValue: true },
];

export function ProductoEstadosAdmin() {
  return <AdminCrudPage title="Estados de producto" endpoint="/admin/producto-estados" fields={productoEstadosFields} keyField="codigo" />;
}

// ───────── Estados de pedido ─────────

const pedidoEstadosFields: AdminField[] = [
  { key: 'codigo', label: 'Código', type: 'text', required: true, hideUpdate: true },
  { key: 'nombre', label: 'Nombre', type: 'text', required: true },
  { key: 'orden', label: 'Orden', type: 'number', defaultValue: 100 },
  { key: 'color', label: 'Color', type: 'color', defaultValue: '#64748b' },
  { key: 'es_terminal', label: 'Es estado terminal', type: 'boolean', defaultValue: false },
];

export function PedidoEstadosAdmin() {
  return <AdminCrudPage title="Estados de pedido" endpoint="/admin/pedido-estados" fields={pedidoEstadosFields} keyField="codigo" />;
}

// ───────── Parámetros ─────────

const parametrosFields: AdminField[] = [
  { key: 'clave', label: 'Clave', type: 'text', required: true, hideUpdate: true },
  { key: 'valor', label: 'Valor', type: 'text', required: true },
  { key: 'tipo', label: 'Tipo', type: 'select', options: [
    { value: 'string', label: 'Texto' },
    { value: 'number', label: 'Número' },
    { value: 'boolean', label: 'Booleano' },
    { value: 'json', label: 'JSON' },
  ], defaultValue: 'string' },
  { key: 'descripcion', label: 'Descripción', type: 'textarea' },
];

export function ParametrosAdmin() {
  return <AdminCrudPage title="Parámetros globales" subtitle="Valores k-v editables que la calculadora de precio usa por defecto." endpoint="/admin/parametros" fields={parametrosFields} keyField="clave" />;
}

// ───────── Envíos ─────────

const enviosFields: AdminField[] = [
  { key: 'tipologia', label: 'Tipología', type: 'text', required: true, hint: 'Ej: "Genérico", "Voluminoso", "Frágil"…' },
  { key: 'descripcion', label: 'Descripción', type: 'textarea' },
  { key: 'coste_unidad_eur', label: 'Coste por unidad (EUR)', type: 'number' },
  { key: 'coste_contenedor_eur', label: 'Coste por contenedor (EUR)', type: 'number' },
  { key: 'unidades_contenedor', label: 'Unidades por contenedor', type: 'number' },
  { key: 'notas', label: 'Notas', type: 'textarea' },
  { key: 'activo', label: 'Activo', type: 'boolean', defaultValue: true },
];

export function EnviosAdmin() {
  return <AdminCrudPage title="Envíos" subtitle="Tabla de costes de envío por tipología de producto." endpoint="/admin/envios" fields={enviosFields} />;
}

// ───────── Condiciones comerciales ─────────

const TARIFA = 'Condiciones cálculo de tarifa';
const FACTURACION = 'Condiciones facturación';
const CLIENTE = 'Condiciones cliente';
const NADA = <span className="text-slate-300">—</span>;

const condicionesFields: AdminField[] = [
  { key: 'marca_id', label: 'Marca', type: 'select', required: true, selectFrom: { endpoint: '/admin/marcas', valueKey: 'id', labelKey: 'nombre' }, section: 'Ámbito' },
  { key: 'gama_id', label: 'Gama (opcional)', type: 'select', selectFrom: { endpoint: '/admin/gamas', valueKey: 'id', labelKey: 'nombre' }, hint: 'En blanco = aplica a todas las gamas.', section: 'Ámbito' },
  { key: 'categoria_id', label: 'Categoría (opcional)', type: 'select', selectFrom: { endpoint: '/admin/categorias', valueKey: 'id', labelKey: 'nombre' }, hint: 'En blanco = aplica a todas las categorías.', section: 'Ámbito' },
  { key: 'subcategoria_id', label: 'Subcategoría (opcional)', type: 'select', selectFrom: { endpoint: '/admin/subcategorias', valueKey: 'id', labelKey: 'nombre' }, hint: 'En blanco = aplica a todas las subcategorías.', section: 'Ámbito' },
  { key: 'margen_pct', label: 'Margen Approx (%)', type: 'number', defaultValue: 0, section: TARIFA },
  {
    key: 'garantiza_margen_minimo',
    label: 'Garantizar margen mínimo Approx',
    type: 'boolean',
    defaultValue: true,
    hint: 'Si está marcado, las cotizaciones que apliquen esta condición ajustan el precio facturado hacia arriba si el margen Approx quedaría por debajo del % de arriba. Editable en cada cotización individual o masiva.',
    wide: true,
    section: TARIFA,
  },
  { key: 'comision_pm_pct', label: 'Comisión PM (%)', type: 'number', defaultValue: 0, section: TARIFA },
  { key: 'rappel_pct', label: 'Rappel (%)', type: 'number', defaultValue: 0, section: TARIFA, hint: 'También es el rappel de facturación.' },
  { key: 'marketing_pct', label: 'Marketing (%)', type: 'number', defaultValue: 0, section: TARIFA, hint: 'También es el marketing de facturación.' },
  { key: 'financiero_pct', label: 'Financiero (%)', type: 'number', defaultValue: 0, section: TARIFA },
  { key: 'foc_target_pct', label: 'FOC target (%)', type: 'number', defaultValue: 0, section: TARIFA },
  { key: 'dias_pago', label: 'Días de pago', type: 'number', section: FACTURACION },
  { key: 'stock_minimo', label: 'Stock mínimo', type: 'number', hint: 'Unidades. Lo usan las sugerencias de reposición si el producto no tiene el suyo.', section: CLIENTE },
  { key: 'margen_target_pvpr_pct', label: 'Margen target PVPr cliente (%)', type: 'number', defaultValue: 0, section: CLIENTE },
  { key: 'dias_rotacion_objetivo', label: 'Días rotación objetivo', type: 'number', section: CLIENTE },
  { key: 'notas', label: 'Notas', type: 'textarea', section: 'Notas' },
];

type CondicionRow = {
  id: string;
  marca_id: string;
  marca_codigo: string | null;
  marca_nombre: string | null;
  cliente_codigo: string | null;
  cliente_nombre: string | null;
  gama_id: string | null;
  gama_nombre: string | null;
  categoria_id: string | null;
  categoria_nombre: string | null;
  subcategoria_id: string | null;
  subcategoria_nombre: string | null;
  margen_pct: number | null;
  garantiza_margen_minimo: boolean | null;
  comision_pm_pct: number | null;
  rappel_pct: number | null;
  marketing_pct: number | null;
  financiero_pct: number | null;
  foc_target_pct: number | null;
  margen_target_pvpr_pct: number | null;
  dias_pago: number | null;
  dias_rotacion_objetivo: number | null;
  stock_minimo: number | null;
  notas: string | null;
};

export function CondicionesComercialesAdmin() {
  const ambito = (label: string | null) => label ?? <span className="italic text-slate-400">— todas —</span>;
  const pct = (key: string, label: string, grupo: string, campo: keyof CondicionRow): Column<CondicionRow> => ({
    key, label, headerGroup: grupo, align: 'right',
    render: (r) => fmt.pct(r[campo] as number | null),
    sortValue: (r) => Number(r[campo]),
  });
  const entero = (key: string, label: string, grupo: string, campo: 'dias_pago' | 'stock_minimo'): Column<CondicionRow> => ({
    key, label, headerGroup: grupo, align: 'right',
    render: (r) => (r[campo] != null ? fmt.int(r[campo]) : NADA),
    sortValue: (r) => (r[campo] == null ? -1 : Number(r[campo])),
  });
  const columns: Column<CondicionRow>[] = [
    {
      key: 'marca',
      label: 'Marca / cliente',
      headerGroup: 'Ámbito', headerGroupColor: 'slate',
      render: (r) => (
        <div>
          <div className="font-medium text-slate-900">{r.marca_nombre ?? '—'}</div>
          {r.cliente_nombre && <div className="text-xs text-slate-500">{r.cliente_nombre}</div>}
        </div>
      ),
      sortValue: (r) => r.marca_nombre ?? '',
    },
    { key: 'gama', label: 'Gama', headerGroup: 'Ámbito', headerGroupColor: 'slate', render: (r) => ambito(r.gama_nombre), sortValue: (r) => r.gama_nombre ?? '' },
    { key: 'categoria', label: 'Categoría', headerGroup: 'Ámbito', headerGroupColor: 'slate', render: (r) => ambito(r.categoria_nombre), sortValue: (r) => r.categoria_nombre ?? '' },
    { key: 'subcategoria', label: 'Subcategoría', headerGroup: 'Ámbito', headerGroupColor: 'slate', render: (r) => ambito(r.subcategoria_nombre), sortValue: (r) => r.subcategoria_nombre ?? '' },
    pct('margen_pct', 'Margen Approx', TARIFA, 'margen_pct'),
    {
      key: 'garantiza_margen_minimo',
      label: 'Garantía mín.',
      headerGroup: TARIFA,
      align: 'center',
      render: (r) => r.garantiza_margen_minimo
        ? <span title="Las cotizaciones que apliquen esta condición intentan garantizar el margen mínimo." className="text-emerald-700">✓</span>
        : NADA,
      sortValue: (r) => r.garantiza_margen_minimo ? 1 : 0,
    },
    pct('comision_pm_pct', 'PM', TARIFA, 'comision_pm_pct'),
    pct('rappel_pct', 'Rappel', TARIFA, 'rappel_pct'),
    pct('marketing_pct', 'MKT', TARIFA, 'marketing_pct'),
    pct('financiero_pct', 'Fin.', TARIFA, 'financiero_pct'),
    pct('foc_target_pct', 'FOC', TARIFA, 'foc_target_pct'),
    entero('dias_pago', 'Días pago', FACTURACION, 'dias_pago'),
    pct('rappel_fact', 'Rappel', FACTURACION, 'rappel_pct'),
    pct('marketing_fact', 'MKT', FACTURACION, 'marketing_pct'),
    entero('stock_minimo', 'Stock mín.', CLIENTE, 'stock_minimo'),
    pct('margen_target_pvpr_pct', 'Margen target', CLIENTE, 'margen_target_pvpr_pct'),
  ];

  return (
    <AdminCrudPage<CondicionRow>
      title="Condiciones comerciales"
      subtitle="Condiciones por marca + ámbito (gama / categoría / subcategoría), en tres grupos: cálculo de tarifa, facturación y cliente. Rappel y MKT son los mismos en tarifa y facturación."
      endpoint="/admin/condiciones-comerciales"
      listEndpoint="/admin/condiciones-comerciales/listado/full"
      fields={condicionesFields}
      columns={columns}
      formColumns={2}
    />
  );
}

// ───────── Calendario fiscal GAME ─────────

const gameCalendarioFields: AdminField[] = [
  { key: 'ano_fiscal', label: 'Año fiscal', type: 'number', required: true, hideUpdate: true, hint: 'PK. No editable tras crear.' },
  { key: 'fecha_inicio', label: 'Fecha inicio (lunes)', type: 'text', required: true, hint: 'YYYY-MM-DD. Debe ser un lunes.' },
  { key: 'fecha_fin', label: 'Fecha fin (domingo)', type: 'text', required: true, hint: 'YYYY-MM-DD. Debe ser un domingo.' },
  { key: 'notas', label: 'Notas', type: 'textarea' },
];

export function GameCalendarioFiscalAdmin() {
  return (
    <AdminCrudPage
      title="Calendario fiscal GAME"
      subtitle="Año fiscal de GAME (inicio: último lunes de abril). Editar si GAME cambia la regla algún año."
      endpoint="/admin/game-calendario-fiscal"
      fields={gameCalendarioFields}
      keyField="ano_fiscal"
    />
  );
}

// ───────── Plantillas de email ─────────

const emailTemplatesFields: AdminField[] = [
  { key: 'nombre', label: 'Nombre', type: 'text', required: true, hint: 'Identificador interno. Ej: "Pedido confirmado", "Solicitud de cotización".' },
  { key: 'asunto', label: 'Asunto', type: 'text', required: true, hint: 'Placeholders disponibles: {{proveedor}}, {{contacto}}, {{email}}.' },
  { key: 'cuerpo', label: 'Cuerpo', type: 'textarea', hint: 'Texto del email. Placeholders: {{proveedor}}, {{contacto}}.' },
  { key: 'destinatario_default', label: 'Destinatario default', type: 'text', hint: 'Opcional. Si está vacío, se usa el email del proveedor.' },
  { key: 'emails_cc_default', label: 'Emails CC default (separados por coma)', type: 'text', hint: 'Se añadirán al CC del proveedor cuando uses la plantilla.' },
  { key: 'activo', label: 'Activo', type: 'boolean', defaultValue: true },
];

export function EmailTemplatesAdmin() {
  return (
    <AdminCrudPage
      title="Plantillas de email"
      subtitle="Plantillas reutilizables para correos a proveedores. Las usa el botón 'Enviar email' de cada ficha."
      endpoint="/admin/email-templates"
      fields={emailTemplatesFields}
    />
  );
}

// ───────── Mapeo de familias TICNOVA ─────────

const ticnovaFamiliasMapFields: AdminField[] = [
  { key: 'des_fam', label: 'Familia TICNOVA (DesFam)', type: 'text', required: true, hint: 'Tal y como viene en el fichero de stock. Ej: "Cables", "Periféricos", "SAIS - UPS".' },
  { key: 'des_subfam', label: 'Subfamilia TICNOVA (DesSubFam)', type: 'text', hint: 'Déjalo vacío para que la regla valga para TODA la familia (se usa como respaldo cuando la subfamilia concreta no está mapeada).' },
  { key: 'gama_id', label: 'Gama', type: 'select', selectFrom: { endpoint: '/admin/gamas', valueKey: 'id', labelKey: 'nombre' }, hint: 'Gama comercial que recibirán estos productos. No se puede deducir del fichero de TICNOVA (salvo Gaming): la decides tú.' },
  { key: 'categoria_id', label: 'Categoría del sistema', type: 'select', selectFrom: { endpoint: '/admin/categorias', valueKey: 'id', labelKey: 'nombre' }, hint: 'Vacío = los productos de esta familia se importan sin clasificar y el importador lo avisa.' },
  { key: 'subcategoria_id', label: 'Subcategoría del sistema', type: 'select', selectFrom: { endpoint: '/admin/subcategorias/opciones', valueKey: 'id', labelKey: 'etiqueta' }, hint: 'Debe pertenecer a la categoría elegida. Vacío si la subfamilia de TICNOVA agrupa cosas dispares.' },
  { key: 'detalle_id', label: 'Detalle (4ª capa)', type: 'select', selectFrom: { endpoint: '/admin/detalles/opciones', valueKey: 'id', labelKey: 'etiqueta' }, hint: 'Debe pertenecer a la subcategoría elegida. Es lo que separa los 5 tipos de cable de TICNOVA, que comparten la subcategoría CABLE.' },
  { key: 'notas', label: 'Notas', type: 'textarea', hint: 'Por qué está sin mapear, o qué criterio se siguió.' },
  { key: 'activo', label: 'Activo', type: 'boolean', defaultValue: true },
];

type TicnovaFamiliaMapRow = {
  id: string;
  des_fam: string;
  des_subfam: string | null;
  cod_fam: string | null;
  cod_subfam: string | null;
  gama_nombre: string | null;
  categoria_nombre: string | null;
  subcategoria_nombre: string | null;
  detalle_nombre: string | null;
  activo: boolean;
  notas: string | null;
};

export function TicnovaFamiliasMapAdmin() {
  const columns: Column<TicnovaFamiliaMapRow>[] = [
    { key: 'des_fam', label: 'Familia TICNOVA', sortValue: (r) => r.des_fam },
    { key: 'des_subfam', label: 'Subfamilia', render: (r) => r.des_subfam ?? <span className="text-slate-400">(toda la familia)</span>, sortValue: (r) => r.des_subfam ?? '' },
    { key: 'cod_fam', label: 'Cód.', render: (r) => <span className="font-mono text-xs">{[r.cod_fam, r.cod_subfam].filter(Boolean).join('/') || '—'}</span> },
    {
      key: 'gama_nombre',
      label: 'Gama',
      render: (r) => r.gama_nombre ?? <span className="text-amber-700">sin gama</span>,
      sortValue: (r) => r.gama_nombre ?? '',
    },
    {
      key: 'categoria_nombre',
      label: 'Categoría',
      render: (r) => r.categoria_nombre ?? <span className="font-medium text-amber-700">sin mapear</span>,
      sortValue: (r) => r.categoria_nombre ?? '',
    },
    {
      key: 'subcategoria_nombre',
      label: 'Subcategoría',
      render: (r) => r.subcategoria_nombre ?? <span className="text-slate-400">—</span>,
      sortValue: (r) => r.subcategoria_nombre ?? '',
    },
    {
      key: 'detalle_nombre',
      label: 'Detalle',
      render: (r) => r.detalle_nombre ?? <span className="text-slate-400">—</span>,
      sortValue: (r) => r.detalle_nombre ?? '',
    },
  ];

  return (
    <AdminCrudPage<TicnovaFamiliaMapRow>
      title="Mapeo de familias TICNOVA"
      subtitle="Traduce la taxonomía de los ficheros de stock de TICNOVA (12 familias / 43 subfamilias) a la cadena completa del sistema: gama → categoría → subcategoría → detalle. Lo usa el importador de /ticnova/importador, que avisa en el preview de cada hueco en vez de inventarse una clasificación."
      endpoint="/admin/ticnova-familias-map"
      fields={ticnovaFamiliasMapFields}
      columns={columns}
      bulkEditEndpoint="/admin/ticnova-familias-map/aplicar-masivo"
      bulkEditKeys={['gama_id', 'categoria_id', 'subcategoria_id', 'detalle_id']}
      mergeNoun="familia"
    />
  );
}

// ── Alias del ERP de APPROX (importador de coste de entrada) ───────────
// Código tal como lo escribe el ERP → producto de la app, o repuesto/ignorar.
// El producto de las líneas ya importadas SIGUE al alias: cambiarlo o
// borrarlo aquí las mueve o las deja sin producto (backfill en el servidor).

type ProductoCodigoAliasRow = {
  id: string; codigo_externo: string; codigo_norm: string; tipo: 'producto' | 'repuesto' | 'ignorar';
  producto_id: string | null; producto_sku: string | null; producto_nombre: string | null; notas: string | null; lineas: number;
};

const productoCodigosAliasFields: AdminField[] = [
  { key: 'codigo_externo', label: 'Código en el ERP', type: 'text', required: true, list: true, hint: 'Tal como aparece en el informe (GM-M27GC7, KX-24…). Mayúsculas y separadores no importan.' },
  { key: 'tipo', label: 'Tipo', type: 'select', required: true, list: true, defaultValue: 'producto', options: [
    { value: 'producto', label: 'Producto' }, { value: 'repuesto', label: 'Repuesto (fuera de costes)' }, { value: 'ignorar', label: 'Ignorar siempre' },
  ] },
  { key: 'producto_id', label: 'Producto', type: 'select', selectFrom: { endpoint: '/productos/listado/full', valueKey: 'id', labelKey: 'sku' }, hint: 'Solo para tipo producto.' },
  { key: 'notas', label: 'Notas', type: 'text' },
];

export function ProductoCodigosAliasAdmin() {
  const columns: Column<ProductoCodigoAliasRow>[] = [
    { key: 'codigo_externo', label: 'Código ERP', render: (r) => <span className="font-mono text-xs">{r.codigo_externo}</span>, sortValue: (r) => r.codigo_externo },
    { key: 'tipo', label: 'Tipo', sortValue: (r) => r.tipo },
    { key: 'producto_sku', label: 'Producto', render: (r) => r.producto_sku ? <><span className="font-mono text-xs">{r.producto_sku}</span> · {r.producto_nombre}</> : <span className="text-slate-400">—</span>, sortValue: (r) => r.producto_sku ?? '' },
    { key: 'lineas', label: 'Líneas', render: (r) => String(r.lineas), sortValue: (r) => r.lineas },
    { key: 'notas', label: 'Notas', render: (r) => r.notas ?? '', sortValue: (r) => r.notas ?? '' },
  ];
  return (
    <AdminCrudPage<ProductoCodigoAliasRow>
      title="Alias de códigos del ERP"
      subtitle="Códigos del ERP de APPROX que el importador de coste de entrada no puede resolver solo, y qué producto de la app son. Cambiar o borrar un alias mueve las líneas ya importadas."
      endpoint="/admin/producto-codigos-alias"
      fields={productoCodigosAliasFields}
      columns={columns}
    />
  );
}

type ProveedorAliasRow = { id: string; alias: string; alias_norm: string; proveedor_id: string; proveedor_nombre: string | null; notas: string | null };

const proveedorAliasFields: AdminField[] = [
  { key: 'alias', label: 'Razón social en el ERP', type: 'text', required: true, list: true, hint: 'Tal como la escribe el ERP (ANJI RUIBAO FURNITURE CO.,LTD).' },
  { key: 'proveedor_id', label: 'Proveedor', type: 'select', required: true, selectFrom: { endpoint: '/proveedores', valueKey: 'id', labelKey: 'nombre' } },
  { key: 'notas', label: 'Notas', type: 'text' },
];

export function ProveedorAliasAdmin() {
  const columns: Column<ProveedorAliasRow>[] = [
    { key: 'alias', label: 'Razón social ERP', render: (r) => <span className="font-mono text-xs">{r.alias}</span>, sortValue: (r) => r.alias },
    { key: 'proveedor_nombre', label: 'Proveedor', render: (r) => r.proveedor_nombre ?? '—', sortValue: (r) => r.proveedor_nombre ?? '' },
    { key: 'notas', label: 'Notas', render: (r) => r.notas ?? '', sortValue: (r) => r.notas ?? '' },
  ];
  return (
    <AdminCrudPage<ProveedorAliasRow>
      title="Alias de proveedores"
      subtitle="Razón social del ERP de APPROX → proveedor de la app. Nunca se crea un proveedor desde el importador: si falta, se da de alta en Proveedores y aquí se enlaza."
      endpoint="/admin/proveedor-alias"
      fields={proveedorAliasFields}
      columns={columns}
    />
  );
}
